/*
  Working out how to save an entry without overlapping the rest of its day,
  for when the admin has turned overlapping entries off. No entry's length is
  ever changed - only where it sits.

  planOverlapFix looks at the day's entries with times and answers one of:

  - { kind: 'clear' }  nothing overlaps; save as asked.
  - { kind: 'slide', startTime, endTime }  the entry being resized fits in the
    free time around it if it moves (8-10 made 3h, with 10-12 taken and the
    morning free, becomes 7-10). Safe to apply without asking.
  - { kind: 'choose', options }  the entry is boxed in on both sides. Each
    option keeps the entry where it was asked to be and moves its neighbours
    along, lengths intact: later ones pushed back, or earlier ones pulled
    forward. Shown to the person to pick.
  - { kind: 'stuck' }  no way to fit it without overlapping (the day is full,
    or a neighbour is locked or comes from time off).
*/

const DAY = 24 * 60;

export const clockToMinutes = (clock) => {
  const [h, m] = String(clock).split(':').map(Number);
  return h * 60 + m;
};

export const minutesToClock = (minutes) => (
  `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`
);

const overlaps = (a, b) => a.start < b.end && b.start < a.end;

const span = (entry) => ({
  entry,
  start: clockToMinutes(entry.startTime),
  end: clockToMinutes(entry.endTime),
  movable: !entry.locked && entry.source !== 'time_off',
});

// Later neighbours shoved back so each starts where the one before it ends.
const pushLater = (target, others) => {
  if (others.some((o) => o.start < target.start && o.end > target.start)) return null;
  const moves = [];
  let cursor = target.end;
  for (const o of others.filter((x) => x.start >= target.start).sort((a, b) => a.start - b.start)) {
    if (o.start >= cursor) break;
    const length = o.end - o.start;
    if (!o.movable || cursor + length > DAY) return null;
    moves.push({ entry: o.entry, start: cursor, end: cursor + length });
    cursor += length;
  }
  // Saved last-first, so no step overlaps the one after it.
  return moves.reverse();
};

// Earlier neighbours pulled forward so each ends where the one after it starts.
const pullEarlier = (target, others) => {
  if (others.some((o) => o.start < target.end && o.end > target.end)) return null;
  const moves = [];
  let cursor = target.start;
  for (const o of others.filter((x) => x.end <= target.end).sort((a, b) => b.end - a.end)) {
    if (o.end <= cursor) break;
    const length = o.end - o.start;
    if (!o.movable || cursor - length < 0) return null;
    moves.push({ entry: o.entry, start: cursor - length, end: cursor });
    cursor -= length;
  }
  return moves.reverse();
};

/**
 * entries:   the day's entries (any without times are ignored)
 * entry:     the one being saved, as it is now
 * startTime / endTime: where it is being asked to go
 * slide:     true when only its length was set, so it may move to fit
 */
export function planOverlapFix({ entries, entry, startTime, endTime, slide = false }) {
  const target = { start: clockToMinutes(startTime), end: clockToMinutes(endTime) };
  const others = entries
    .filter((e) => e.id !== entry.id && e.startTime && e.endTime)
    .map(span);
  if (!others.some((o) => overlaps(o, target))) return { kind: 'clear' };

  const length = target.end - target.start;

  if (slide && entry.startTime && entry.endTime) {
    // Anywhere that still touches where it was, nearest the asked-for start
    // first: keep the start, else keep the end, else snug against a neighbour.
    const was = span(entry);
    const candidates = [
      target.start,
      was.end - length,
      ...others.map((o) => o.end),
      ...others.map((o) => o.start - length),
    ];
    const fits = candidates
      .filter((s) => s >= 0 && s + length <= DAY && s <= was.end && s + length >= was.start)
      .filter((s) => !others.some((o) => overlaps(o, { start: s, end: s + length })))
      .sort((a, b) => Math.abs(a - target.start) - Math.abs(b - target.start));
    if (fits.length) {
      return { kind: 'slide', startTime: minutesToClock(fits[0]), endTime: minutesToClock(fits[0] + length) };
    }
  }

  const toClocks = (m) => ({ entry: m.entry, startTime: minutesToClock(m.start), endTime: minutesToClock(m.end) });
  const options = [];

  const later = pushLater(target, others);
  if (later && later.length) {
    options.push({ key: 'later', startTime, endTime, moves: later.map(toClocks) });
  }

  // When only the length changed, pulling earlier keeps the entry's end and
  // grows it backwards instead.
  const was = slide && entry.startTime && entry.endTime ? span(entry) : null;
  const earlierTarget = was ? { start: was.end - length, end: was.end } : target;
  if (earlierTarget.start >= 0) {
    const earlier = pullEarlier(earlierTarget, others);
    if (earlier && earlier.length) {
      options.push({
        key: 'earlier',
        startTime: minutesToClock(earlierTarget.start),
        endTime: minutesToClock(earlierTarget.end),
        moves: earlier.map(toClocks),
      });
    }
  }

  return options.length ? { kind: 'choose', options } : { kind: 'stuck' };
}

// The first of the day's entries with times that [startTime, endTime) would
// overlap, or null.
export function findOverlap({ entries, startTime, endTime, excludeId = null }) {
  if (!startTime || !endTime) return null;
  const target = { start: clockToMinutes(startTime), end: clockToMinutes(endTime) };
  if (!(target.end > target.start)) return null;
  return entries
    .filter((e) => e.id !== excludeId && e.startTime && e.endTime)
    .map(span)
    .sort((a, b) => a.start - b.start)
    .find((o) => overlaps(o, target))?.entry || null;
}

// The nearest free stretch of the day that holds the same length - right
// after or right before one of the day's entries, closest to where it was
// asked for (later wins a tie). Null when the day has no room for it.
export function findOpenSlot({ entries, startTime, endTime, excludeId = null }) {
  const start = clockToMinutes(startTime);
  const length = clockToMinutes(endTime) - start;
  if (!(length > 0)) return null;
  const others = entries.filter((e) => e.id !== excludeId && e.startTime && e.endTime).map(span);
  const best = [start, ...others.map((o) => o.end), ...others.map((o) => o.start - length)]
    .filter((s) => s >= 0 && s + length <= DAY)
    .filter((s) => !others.some((o) => overlaps(o, { start: s, end: s + length })))
    .sort((a, b) => Math.abs(a - start) - Math.abs(b - start) || b - a)[0];
  return best === undefined ? null : { startTime: minutesToClock(best), endTime: minutesToClock(best + length) };
}
